/**
 * The link(s) that get someone into a meeting.
 *
 * A meeting normally has one, `/meeting/:id`, and everyone who opens it is a
 * full participant. A meeting with passive attendees enabled has two, told
 * apart by an opaque key the server issued — one grants full participation, the
 * other view-only.
 *
 * The key is in the URL because the URL is the thing that gets pasted into a
 * calendar invite; it is unguessable rather than descriptive (`?mode=passive`
 * would announce exactly what to delete) and the server checks it on join, so
 * removing or editing it drops the visitor back to the default rather than
 * promoting them.
 */

export const JOIN_KEY_PARAM = 'k'

/**
 * The role marker, carried when the server has not issued keys.
 *
 * Mirrors what inmeet_schedule_api did — `Buffer.from('Participant')` in the
 * path of the passive link, `'guest'` in the active one. Base64 there was
 * encoding rather than encryption, and it is no different here: anyone can
 * decode the value and swap it. It marks which audience a link was sent to; it
 * does not keep anyone in that audience.
 *
 * Kept because it needs nothing from the server, so the two links exist as soon
 * as the option is switched on. The moment a meeting has real keys they are
 * used instead — see `buildJoinLinks` — and only those are checked on join.
 */
export const ROLE_PARAM = 'role'
export const PASSIVE_ROLE = btoa('Participant')

/**
 * Is the address bar a passive link, right now?
 *
 * Deliberately a plain function of the URL rather than something read from
 * state, because the answer is needed *before* React state exists. The prejoin
 * screen opens the camera from a mount effect, and child effects run before
 * parent ones — so anything that seeds a store from an effect higher up has
 * already lost the race, and the permission prompt is on screen before the flag
 * it was supposed to be gated on gets set.
 */
export function isPassiveJoinUrl(search?: string): boolean {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(search ?? window.location.search).get(ROLE_PARAM) === PASSIVE_ROLE
}

export type JoinLinks =
  | { split: false; active: string }
  | { split: true; active: string; passive: string }

interface LinkableMeeting {
  meetingId: string
  activeKey?: string
  passiveKey?: string
  settings?: { passiveParticipantsEnabled?: boolean }
}

export function buildJoinLinks(meeting: LinkableMeeting, baseUrl: string): JoinLinks {
  const base = `${baseUrl}/meeting/${meeting.meetingId}`

  // The option is what decides whether there are two audiences. Whether the
  // server has issued keys only decides how good the difference between the two
  // links is.
  if (!meeting.settings?.passiveParticipantsEnabled) {
    return { split: false, active: base }
  }

  // Real keys when the meeting has them: unguessable, checked on join, and the
  // only version anything is actually enforced against.
  if (meeting.activeKey && meeting.passiveKey) {
    return {
      split: true,
      active: `${base}?${JOIN_KEY_PARAM}=${encodeURIComponent(meeting.activeKey)}`,
      passive: `${base}?${JOIN_KEY_PARAM}=${encodeURIComponent(meeting.passiveKey)}`,
    }
  }

  // Otherwise the role marker, which needs nothing from the server. Both keys
  // or neither — one key alone would mean one real link and one guess, and the
  // pair would not be telling the truth about which is which.
  return {
    split: true,
    active: base,
    passive: `${base}?${ROLE_PARAM}=${PASSIVE_ROLE}`,
  }
}
